import { type JSX, type Ref, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import {
  CustomSelect,
  type RenderSearchItemProps,
} from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { type Collaborator } from '@/features/jobs/Job'
import {
  RemoveCollaboratorSearchItem,
  RemoveCollaboratorsAsAdminDialog,
} from '@/features/jobs/details/components/RemoveCollaboratorsAsAdminDialog'
import { type RemoveCollaboratorsAsAdminDialogViewModel } from './RemoveCollaboratorsAsAdminDialogViewModel'

export interface RemoveCollaboratorsAsAdminDialogContainerProps {
  vm: RemoveCollaboratorsAsAdminDialogViewModel
}

export const RemoveCollaboratorsAsAdminDialogContainer = observer(
  function RemoveCollaboratorsAsAdminDialogContainer({
    vm,
  }: RemoveCollaboratorsAsAdminDialogContainerProps): JSX.Element {
    const itemSource = useMemo(
      () =>
        ItemSource.static<Collaborator>(
          vm.filteredCollaborators,
          (item, text) =>
            item.member.displayName.toLowerCase().includes(text.toLowerCase()),
        ),
      [vm.filteredCollaborators],
    )

    const selectProps = useCustomSelectStateFromItemSource({
      itemSource,
    })

    return (
      <RemoveCollaboratorsAsAdminDialog
        showing={vm.modal.showing}
        collaboratorError={vm.validation.getError('collaborator')}
        collaboratorSelect={
          <CustomSelect
            {...selectProps}
            multi={false}
            icon="dropdown"
            iconColor="var(--grey600)"
            itemToString={(i) => i.member.displayName}
            placeholder="Select collaborator"
            value={vm.collaborator}
            onChange={vm.setCollaborator}
            renderSearchItem={(props) => (
              <RemoveSearchItemContainer {...props} />
            )}
            testId="remove-job-collaborator-as-admin-dropdown"
          />
        }
        reasonField={
          <TextAreaContainer
            model={vm}
            field="reason"
            name="reason"
            error={vm.validation.getError('reason')}
            data-test="remove-job-collaborator-as-admin-reason-input"
            rows={4}
          />
        }
        reasonHelpText={vm.reasonHelpText}
        isRemovePending={vm.removeCollaboratorAsAdmin.pending}
        isRemoveRejected={vm.removeCollaboratorAsAdmin.rejected}
        removeError={vm.removeCollaboratorAsAdmin.error}
        isDisabled={vm.disabled}
        onClose={vm.modal.close}
        onRemove={vm.removeCollaboratorAsAdmin}
        submitButtonTestId="remove-job-collaborator-as-admin-submit-button"
      />
    )
  },
)

interface RemoveSearchItemContainerProps extends RenderSearchItemProps<Collaborator> {
  ref?: Ref<HTMLDivElement>
}

const RemoveSearchItemContainer = observer(function RemoveSearchItemContainer({
  item,
  itemToString: _itemToString,
  ref,
  ...itemProps
}: RemoveSearchItemContainerProps) {
  return (
    <RemoveCollaboratorSearchItem
      ref={ref}
      {...itemProps}
      testId={
        'remove-job-collaborator-as-admin--option-' +
        item.member.internalId?.toLowerCase()
      }
    >
      {item.member.displayName}
    </RemoveCollaboratorSearchItem>
  )
})
